/**
 * Experience Video Player Component
 * 
 * Video overlay player for experience mode videos
 * Auto-fades back to experience mode when video ends
 */

import React, { useEffect, useRef, useState } from 'react';
import { ExperienceSpot } from '../../types/experienceMode.types';
import { useExperienceMode } from './ExperienceModeProvider';

interface ExperienceVideoPlayerProps {
  spot: ExperienceSpot;
}

export const ExperienceVideoPlayer: React.FC<ExperienceVideoPlayerProps> = ({ spot }) => {
  const { closeVideo, markSpotAsSeen } = useExperienceMode();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isFadingOut, setIsFadingOut] = useState(false);

  // Handle ESC key to close video
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const handleClose = () => {
    // Pause video before closing to prevent "aborted by user agent" error
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute('src');
      videoRef.current.load();
    }
    setIsFadingOut(true);
    setTimeout(() => {
      closeVideo();
    }, 500);
  };

  // Auto-close with fade when video ends
  const handleVideoEnd = () => {
    markSpotAsSeen(spot.id);
    // Small delay so user sees it finished, then fade out
    setTimeout(() => {
      handleClose();
    }, 400);
  };

  return (
    <div className={`experience-video-overlay ${isFadingOut ? 'fade-out' : ''}`}>
      {/* Backdrop */}
      <div className="experience-video-backdrop" onClick={handleClose} />
      
      {/* Video Container */}
      <div className={`experience-video-container ${isFadingOut ? 'fade-out' : ''}`}>
        {/* Close Button */}
        <button className="experience-video-close" onClick={handleClose}>
          <span>✕</span>
        </button>
        
        {spot.videoUrl ? (
          /* Video only */
          <video
            ref={videoRef}
            src={spot.videoUrl}
            controls
            autoPlay
            className="experience-video"
            onEnded={handleVideoEnd}
            onError={(e) => {
              // Suppress "aborted by user agent" errors (normal when closing video)
              const target = e.target as HTMLVideoElement;
              if (target.error?.code === MediaError.MEDIA_ERR_ABORTED) return;
              console.error('Video load error:', e);
            }}
          >
            Your browser does not support the video tag.
          </video>
        ) : (
          /* No video URL fallback */
          <div style={{ 
            padding: '3rem', 
            textAlign: 'center', 
            color: 'white',
            background: 'rgba(0,0,0,0.8)',
            borderRadius: '20px'
          }}>
            <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>🎬 {spot.title}</p>
            <p style={{ opacity: 0.7 }}>Nog geen video geüpload voor deze spot.</p>
            <button 
              onClick={handleClose}
              style={{ 
                marginTop: '1rem', 
                padding: '0.5rem 1.5rem', 
                borderRadius: '8px', 
                background: '#10b981', 
                color: 'white', 
                border: 'none', 
                cursor: 'pointer' 
              }}
            >
              Sluiten
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
